import React, { useContext, useEffect, useState } from 'react';
import {
  DeleteOutlined,
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, Drawer, Popconfirm, Space, Spin, Tag } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'dayjs';

import {
  Initiative,
  Program,
  STRATEGIES,
} from '@totem/components/portfolio/buildings/details/optimize/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const statusColors: Record<string, string> = {
  proposed: 'blue',
  active: 'green',
  suspended: 'orange',
};

const strategyLabel = (val: string) =>
  STRATEGIES.find((strategy) => strategy.value === val)?.label ?? val;

type Props = {
  open: boolean;
  buildingId: string;
  program: Program;
  refreshTrigger: number;
  onClose: () => void;
  onRequestAdd: () => void;
  onRequestEdit: (initiative: Initiative) => void;
};

const InitiativesDrawer = ({
  open,
  buildingId,
  program,
  refreshTrigger,
  onClose,
  onRequestAdd,
  onRequestEdit,
}: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [records, setRecords] = useState<Initiative[]>([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    if (open && program) {
      setRefresh(true);
    }
  }, [open, program]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      setRefresh(true);
    }
  }, [refreshTrigger]);

  useEffect(() => {
    if (!refresh || !program) {
      return;
    }
    setRefresh(false);

    setLoading(true);

    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/initiatives?programId=${program.id}`,
      {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => res.json())
      .then((result: Initiative[]) => {
        setRecords(Array.isArray(result) ? result : []);
      })
      .finally(() => setLoading(false));
  }, [refresh, buildingId, program, getApiToken]);

  const handleDelete = (record: Initiative) => {
    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/initiatives/${record.id}`,
      {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    ).then((res) => {
      CheckResponseShowError(res);

      setRefresh(true);
    });
  };

  const handleSuspend = (record: Initiative) => {
    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/initiatives/${record.id}/suspend`,
      {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    ).then((res) => {
      CheckResponseShowError(res);

      setRefresh(true);
    });
  };

  const handleReactivate = (record: Initiative) => {
    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/initiatives/${record.id}/reactivate`,
      {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    ).then((res) => {
      CheckResponseShowError(res);

      setRefresh(true);
    });
  };

  const columns: ColumnProps<Initiative>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Strategy',
      dataIndex: 'strategy',
      key: 'strategy',
      render: (_, record) => strategyLabel(record.strategy),
    },
    {
      title: 'Charge',
      dataIndex: 'chargeType',
      key: 'chargeType',
      width: 90,
      render: (_, record) => (
        <Tag color={record.chargeType === 'energy' ? 'blue' : 'purple'}>
          {record.chargeType}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Tag color={statusColors[record.status] ?? 'default'}>
          {record.status}
        </Tag>
      ),
    },
    {
      title: 'Effective Date',
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      render: (_, record) => dayjs(record.effectiveDate).format('MMM D, YYYY'),
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_, record) => (
        <Space>
          {record.status === 'active' && (
            <Popconfirm
              title="Suspend this initiative?"
              onConfirm={() => handleSuspend(record)}
              okText="Suspend"
              okType="danger"
            >
              <Button size="small" icon={<PauseCircleOutlined />}>
                Suspend
              </Button>
            </Popconfirm>
          )}
          {record.status === 'suspended' && (
            <Button
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleReactivate(record)}
            >
              Reactivate
            </Button>
          )}
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => onRequestEdit(record)}
          />
          <Popconfirm
            title="Delete this initiative?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Initiatives — ${program?.name ?? ''}`}
      width={900}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={onRequestAdd}>
          Add Initiative
        </Button>
      }
    >
      <Spin spinning={loading}>
        <IBTable
          columns={columns}
          dataSource={records}
          rowKey={(record) => record.id}
          pagination={{ showSizeChanger: true, pageSize: 20 }}
          size="small"
        />
      </Spin>
    </Drawer>
  );
};

export default InitiativesDrawer;
